import { ChangeDetectionStrategy, Component } from '@angular/core';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { heroCog6Tooth } from '@ng-icons/heroicons/outline';
import { TranslatePipe } from '../../pipes/translate.pipe';

@Component({
  selector: 'app-loader',
  imports: [NgIconComponent, TranslatePipe],
  providers: [provideIcons({ heroCog6Tooth })],
  template: `
    <div class="loader" role="status" [attr.aria-label]="'common.loading' | translate">
      <div class="loader__emblem" aria-hidden="true">
        <span class="loader__halo"></span>
        <span class="loader__ring loader__ring--outer"></span>
        <span class="loader__ring loader__ring--inner"></span>
        <ng-icon class="loader__gear" name="heroCog6Tooth" size="30" />
      </div>

      <p class="loader__label">
        {{ 'common.loading' | translate }}
      </p>
    </div>
  `,
  styles: `
    @use 'variables' as *;

    .loader {
      position: fixed;
      inset: 0;
      z-index: 10000;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: $df-spacing-5;
      background:
        radial-gradient(circle at 50% 42%, rgba($df-color-primary-100, 0.55), transparent 55%),
        rgba($df-brand-blanc, 0.72);
      backdrop-filter: blur(10px);
      animation: fade-in 0.25s ease-out;
    }

    .loader__emblem {
      position: relative;
      display: grid;
      place-items: center;
      width: 92px;
      height: 92px;
    }

    .loader__halo {
      position: absolute;
      inset: 14px;
      border-radius: 50%;
      background: $df-brand-blanc;
      box-shadow:
        0 10px 30px rgba($df-brand-bleu-espace, 0.16),
        inset 0 0 0 1px rgba($df-brand-bleu-espace, 0.06);
      animation: breathe 2.4s ease-in-out infinite;
    }

    .loader__ring {
      position: absolute;
      border-radius: 50%;
      // * Keeps only a thin outline of the gradient disc, so it reads as an arc.
      mask: radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 2px));
    }

    .loader__ring--outer {
      inset: 0;
      background: conic-gradient(from 0deg, transparent 0 30%, $df-color-primary, $df-brand-bleu-espace);
      animation: spin 1.5s linear infinite;
    }

    .loader__ring--inner {
      inset: 8px;
      background: conic-gradient(from 180deg, transparent 0 55%, rgba($df-color-primary, 0.55));
      animation: spin 2.4s linear infinite reverse;
    }

    .loader__gear {
      position: relative;
      color: $df-brand-bleu-espace;
      animation: spin 6s linear infinite;
    }

    .loader__label {
      margin: 0;
      color: $df-color-text-tertiary;
      font-size: $df-font-size-xs;
      font-weight: $df-font-weight-semibold;
      letter-spacing: 0.18em;
      text-transform: uppercase;
      animation: pulse 1.6s ease-in-out infinite;
    }

    @keyframes spin {
      to {
        transform: rotate(360deg);
      }
    }

    @keyframes breathe {
      0%,
      100% {
        transform: scale(1);
      }
      50% {
        transform: scale(1.06);
      }
    }

    @keyframes pulse {
      0%,
      100% {
        opacity: 0.45;
      }
      50% {
        opacity: 1;
      }
    }

    @keyframes fade-in {
      from {
        opacity: 0;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .loader,
      .loader__halo,
      .loader__ring,
      .loader__gear,
      .loader__label {
        animation: none;
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Loader {}
